"use client";

import { useState } from "react";
import Link from "next/link";
import { ArrowRight, ArrowUpRight } from "lucide-react";

import { ThemeToggle } from "@/components/dashboard/theme-toggle";
import { DocsSearch } from "@/components/docs/docs-search";
import { LeadCaptureModal } from "@/components/lead-capture-modal";
import { DOCS_BASE } from "@/app/(marketing)/docs/_config";
import { useSignedInHint } from "@/hooks/use-signed-in-hint";

const primaryCta =
  "bg-rl-accent hover:bg-rl-accent-bright inline-flex items-center gap-1 rounded-full px-3.5 py-1.5 text-[0.8125rem] font-bold whitespace-nowrap text-[#08090c] transition-colors";

/**
 * Docs utility bar. Full-width and flush to the top, with its bottom
 * border running edge to edge so it visually caps both the sidebar and
 * the article column. The inner row shares the shell's max width so the
 * wordmark lines up above the sidebar. Intentionally carries no
 * marketing nav: search is the centerpiece, and there is one primary
 * action plus a single escape hatch back to the marketing site.
 */
export function DocsHeader() {
  const signedIn = useSignedInHint();
  const [inquiryOpen, setInquiryOpen] = useState(false);

  return (
    <header className="border-rl-border bg-rl-bg/80 sticky top-0 z-50 w-full border-b backdrop-blur-xl">
      <div className="mx-auto flex h-14 max-w-[88rem] items-center gap-2 px-4 sm:gap-4 md:px-6">
        {/* Left cluster — width matches the sidebar column on large screens */}
        <div className="flex shrink-0 items-center gap-2 lg:w-60">
          <Link
            href={DOCS_BASE}
            className="text-foreground text-[1.2rem] font-extrabold tracking-[-0.03em]"
          >
            raileon<span className="text-rl-accent">.</span>
          </Link>
          <span className="border-rl-border bg-rl-surface/60 text-rl-text2 rounded-full border px-2 py-0.5 text-[0.65rem] font-semibold tracking-wide">
            Docs
          </span>
        </div>

        {/* Center — search is the centerpiece (icon-only below sm so the
            right cluster still fits a 375px phone) */}
        <div className="flex min-w-0 flex-1 justify-center">
          <DocsSearch />
        </div>

        {/* Right cluster — one primary action, theme toggle, escape hatch */}
        <div className="flex shrink-0 items-center justify-end gap-1.5 lg:min-w-52">
          <Link
            href="/"
            className="text-rl-text3 hover:text-rl-text2 hidden items-center gap-0.5 rounded-md px-2 py-1.5 text-[0.8rem] transition-colors sm:inline-flex"
          >
            raileon.com
            <ArrowUpRight size={13} aria-hidden />
          </Link>
          <ThemeToggle />
          {/* Accounts are invite-only, so signed-out visitors get the
              inquiry form rather than a sign-up page. */}
          {signedIn ? (
            <Link href="/dashboard" className={primaryCta}>
              Dashboard
              <ArrowRight size={14} aria-hidden />
            </Link>
          ) : (
            <button
              type="button"
              onClick={() => setInquiryOpen(true)}
              className={`${primaryCta} cursor-pointer`}
            >
              Get started
              <ArrowRight size={14} aria-hidden />
            </button>
          )}
        </div>
      </div>
      <LeadCaptureModal open={inquiryOpen} onOpenChange={setInquiryOpen} />
    </header>
  );
}
